// RoleBadge component - Premium gradient badge for user roles
// CREATED: Distinctive visual treatment for role badges to differentiate from service badges
// Uses: Pastel gradient backgrounds, icons, and premium styling for clear role identification
// Roles use rectangular badges with soft pastel gradients, Services use capsule badges with pastels
// UPDATED: Changed from vibrant gradients to softer pastel gradients with darker text for better readability

import { Shield, Crown, Users, User } from 'lucide-react';
import type { UserRole } from '../../lib/roleThemes';
import { getRoleTheme } from '../../lib/roleThemes';

interface RoleBadgeProps {
  role: UserRole;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const roleIcons = {
  admin: Shield,
  director: Crown,
  manager: Users,
  operator: User,
};

const roleTextColors = {
  admin: 'text-rose-800',
  director: 'text-amber-800',
  manager: 'text-emerald-800',
  operator: 'text-blue-800',
};

const sizeClasses = {
  sm: {
    container: 'px-2 py-0.5 text-xs gap-1',
    icon: 'w-3 h-3',
  },
  md: {
    container: 'px-3 py-1 text-xs gap-1.5',
    icon: 'w-3.5 h-3.5',
  },
  lg: {
    container: 'px-3 py-1.5 text-sm gap-2',
    icon: 'w-4 h-4',
  },
};

export default function RoleBadge({
  role,
  size = 'md',
  showIcon = true
}: RoleBadgeProps) {
  const theme = getRoleTheme(role);
  const Icon = roleIcons[role];
  const sizes = sizeClasses[size];
  const textColor = roleTextColors[role];

  return (
    <span
      className={`
        inline-flex items-center rounded-lg font-semibold
        bg-gradient-to-r ${theme.gradient}
        ${textColor} shadow-sm
        ${sizes.container}
      `}
    >
      {showIcon && <Icon className={sizes.icon} />}
      <span>{theme.displayName}</span>
    </span>
  );
}
